// src/redux/companySlice.js
import api from './config';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from './authSlice';

const initialState = {
  companies: [],
  company: null,
  loading: false,
  isSuccess: false,
  message: '',
  errors: [],
  favoris: {},

  // Lost items
  lostItems: [],
  lostLoading: false,
  lostError: '',

  // ✅ Jobs / Offres
  jobs: [],
  jobDetails: null,
  jobsLoading: false,
  jobsError: ''
};

export const fetchCompanies = createAsyncThunk(
  'company/fetchCompanies',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/company/getAll');
      return response.data;
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchCompanyByName = createAsyncThunk(
  'company/fetchCompanyByName',
  async ({ message }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/company/getByName/${message}`);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchCompanyById = createAsyncThunk(
  'company/fetchCompanyById',
  async ({ companyid }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/company/${companyid}`);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const addFavoris = createAsyncThunk(
  'company/addFavoris',
  async ({ id }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(`/user-favoris/add-favoris/${id}`);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const removeFavoris = createAsyncThunk(
  'company/removeFavoris',
  async ({ id }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.delete(`/user-favoris/remove-favoris/${id}`);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const getFavoris = createAsyncThunk(
  'company/getFavoris',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/user-favoris/get-liste-favoris`);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchCompanyLostItems = createAsyncThunk(
  'company/fetchCompanyLostItems',
  async ({ companyId }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await api.get(`/company/lost-items/company/${companyId}`);
      return Array.isArray(data) ? data : data?.items || [];
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

/* ===================== ✅ 1. Liste des offres ===================== 
export const fetchCompanyOffers = createAsyncThunk(
  'company/fetchCompanyOffers',
  async (
    { companyId, status, sortBy = 'createdAt', order = 'desc' },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const query = new URLSearchParams();
      if (Array.isArray(status) && status.length) {
        status.forEach((s) => query.append('status', s));
      }
      if (sortBy) query.set('sortBy', sortBy);
      if (order) query.set('order', order);
      const qs = query.toString();

      const url = qs
        ? `/collaboration/offers/company/${companyId}?${qs}`
        : `/collaboration/offers/company/${companyId}`;

      const { data } = await api.get(url);
      return Array.isArray(data)
        ? data
        : (Array.isArray(data?.data) && data.data) || data?.items || [];
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error?.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);
*/
export const fetchCompanyOffers = createAsyncThunk(
  'company/fetchCompanyOffers',
  async ({ companyId }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await api.get(`/job-posts/company/${companyId}`);
      return Array.isArray(data) ? data : data?.data || data?.items || [];
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error?.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);
/* ===================== ✅ 2. Détails d'une offre ===================== */
export const fetchCompanyOfferDetails = createAsyncThunk(
  'company/fetchCompanyOfferDetails',
  async ({ companyId, offerId }, { rejectWithValue, dispatch }) => {
    try {
      const url = `/collaboration/offers/company/${companyId}/${offerId}`;
      const { data } = await api.get(url);
      return data;
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error?.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

/* ===================== Slice ===================== */

const companiesSlice = createSlice({
  name: 'company',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      /* --- Companies --- */
      .addCase(fetchCompanies.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCompanies.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.companies = payload?.data || [];
        state.recordsTotal = payload?.recordsTotal || 0;
        state.loading = false;
      })
      .addCase(fetchCompanies.rejected, (state, { payload }) => {
        state.isSuccess = false;
        state.errors = payload?.message;
        state.loading = false;
      })

      /* --- Company By ID --- */
      .addCase(fetchCompanyById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCompanyById.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.company = payload || null;
        state.loading = false;
      })
      .addCase(fetchCompanyById.rejected, (state, { payload }) => {
        state.isSuccess = false;
        state.errors = payload?.message;
        state.loading = false;
      })

      /* --- Favoris --- */
      .addCase(getFavoris.fulfilled, (state, { payload }) => {
        state.favoris = payload || null;
      })

      /* --- Lost Items --- */
      .addCase(fetchCompanyLostItems.pending, (state) => {
        state.lostLoading = true;
      })
      .addCase(fetchCompanyLostItems.fulfilled, (state, { payload }) => {
        state.lostLoading = false;
        state.lostItems = payload || [];
      })
      .addCase(fetchCompanyLostItems.rejected, (state, { payload }) => {
        state.lostLoading = false;
        state.lostError = payload?.message || 'Erreur lors du chargement';
      })

      /* --- ✅ Jobs / Offers --- */
      .addCase(fetchCompanyOffers.pending, (state) => {
        state.jobsLoading = true;
        state.jobsError = '';
      })
      .addCase(fetchCompanyOffers.fulfilled, (state, { payload }) => {
        state.jobsLoading = false;
        state.jobs = payload || [];
      })
      .addCase(fetchCompanyOffers.rejected, (state, { payload }) => {
        state.jobsLoading = false;
        state.jobsError = payload?.message || 'Erreur lors du chargement des offres';
      })

      /* --- ✅ Job Offer Details --- */
      .addCase(fetchCompanyOfferDetails.pending, (state) => {
        state.jobsLoading = true;
        state.jobDetails = null;
      })
      .addCase(fetchCompanyOfferDetails.fulfilled, (state, { payload }) => {
        state.jobsLoading = false;
        state.jobDetails = payload || null;
      })
      .addCase(fetchCompanyOfferDetails.rejected, (state, { payload }) => {
        state.jobsLoading = false;
        state.jobsError = payload?.message || 'Erreur lors du chargement du détail';
      });
  }
});

export default companiesSlice.reducer;
